import { el } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { navigate } from '../../core/router.js';
import {
  oauthCompletePayload,
  oauthStartUrl,
  parseAuthSearch,
  postLoginPath,
  signupPayload,
  validateSignup,
  workspaceSlugPreview,
} from './model.js';
import { authShell, errorBox, setError, socialRow } from './layout.js';
import { LANGUAGES, languageOptionLabel } from '../../core/languages.js';

function languageSelect(id, name) {
  return el('select', {
    class: 'auth-fld',
    id,
    name: name || 'chatLanguage',
  },
  ...LANGUAGES.map((lang) => el('option', {
    value: lang.code,
    selected: lang.code === 'en' ? true : null,
  }, languageOptionLabel(lang))));
}

async function rememberSession(data) {
  localStorage.setItem('accessToken', data.accessToken);
  if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
  if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
}

async function goAfterSignup() {
  const products = await api('/products');
  const list = products && Array.isArray(products.products) ? products.products : [];
  window.location.href = postLoginPath({ hasProducts: list.length > 0 });
}

function providerMeta(provider) {
  const id = String(provider || '').toLowerCase();
  if (id === 'google') return { name: 'Google', initial: 'G', bg: '#ef4444' };
  if (id === 'microsoft') return { name: 'Microsoft', initial: 'M', bg: '#0ea5e9' };
  if (id === 'github') return { name: 'GitHub', initial: 'G', bg: '#18181b' };
  return { name: provider || 'SSO', initial: (provider || 'S').charAt(0).toUpperCase(), bg: 'var(--accent)' };
}

/**
 * OAuth complete-signup: name the workspace after the provider handshake.
 */
export function renderOAuthWorkspace() {
  const err = errorBox('');
  const pendingMount = el('div', { class: 'auth-oauth-pending' }, el('div', { class: 'auth-spinner' }));
  const nameHidden = el('input', { type: 'hidden', name: 'name', value: '' });
  const org = el('input', {
    class: 'auth-fld',
    type: 'text',
    name: 'orgName',
    id: 'oauth-org',
    placeholder: 'Enter your company name',
    required: true,
    maxlength: '80',
    autocomplete: 'organization',
  });
  const chatLanguage = languageSelect('oauth-chat-language', 'chatLanguage');
  const slug = el('span', { id: 'oauth-slug-preview' }, 'app.bonnieos.com/your-workspace');
  const submit = el('button', {
    type: 'submit',
    class: 'auth-go auth-go-block',
    id: 'oauth-workspace-btn',
    disabled: true,
  }, 'Create workspace');

  function refreshSlug() {
    const ready = !!String(org.value || '').trim();
    submit.disabled = !ready;
    submit.classList.toggle('is-ready', ready);
    slug.textContent = `app.bonnieos.com/${workspaceSlugPreview(org.value)}`;
  }
  org.addEventListener('input', refreshSlug);

  const form = el('form', {
    class: 'auth-form',
    hidden: true,
    onSubmit: async (event) => {
      event.preventDefault();
      const body = oauthCompletePayload({
        orgName: org.value,
        name: nameHidden.value,
        chatLanguage: chatLanguage.value,
      });
      if (!body.orgName) {
        setError(err, 'Workspace name is required');
        return;
      }
      setError(err, '');
      submit.disabled = true;
      submit.textContent = 'Creating…';
      const data = await api('/auth/oauth/complete-signup', { method: 'POST', body });
      if (data && data.error) {
        setError(err, data.error);
        submit.disabled = false;
        submit.textContent = 'Create workspace';
        return;
      }
      if (!data || !data.accessToken) {
        setError(err, 'Could not create workspace');
        submit.disabled = false;
        submit.textContent = 'Create workspace';
        return;
      }
      await rememberSession(data);
      window.location.href = postLoginPath({ hasProducts: false });
    },
  },
  nameHidden,
  el('label', { class: 'auth-field' },
    el('span', { class: 'lbl auth-field-lbl' }, 'Workspace name'),
    org),
  el('label', { class: 'auth-field' },
    el('span', { class: 'lbl auth-field-lbl' }, 'Preferred chat language'),
    chatLanguage),
  el('div', { class: 'auth-slug-line' }, 'Your workspace URL: ', slug),
  submit,
  el('p', { class: 'auth-note' },
    'You can invite teammates and change this name later from Settings → Organisation.'),
  el('p', { class: 'auth-alt' },
    'Wrong account? ',
    el('a', {
      href: '/app/signup',
      class: 'auth-lnk',
      onClick(event) {
        event.preventDefault();
        navigate('/app/signup');
      },
    }, 'Start over')));

  const root = authShell({
    variant: 'workspace',
    kicker: '✦ Workspace',
    title: 'Name your ',
    titleEm: 'workspace.',
    lead: 'One more step — give your workspace a name so your team can find it.',
    error: err,
    children: [pendingMount, form],
  });

  (async () => {
    const pending = await api('/auth/oauth/pending');
    if (!pending || !pending.pending) {
      pendingMount.replaceChildren();
      setError(err, 'Social sign-in expired. Continue with Google, Microsoft, or GitHub again.');
      form.hidden = true;
      return;
    }
    const meta = providerMeta(pending.provider);
    pendingMount.replaceChildren(
      el('div', { class: 'auth-provider-mark', style: `background:${meta.bg}`, 'aria-hidden': 'true' }, meta.initial),
      el('p', { class: 'auth-lead' },
        `Signed in with ${meta.name} as ${pending.email}.`),
    );
    nameHidden.value = pending.name || '';
    form.hidden = false;
    refreshSlug();
    setTimeout(() => org.focus(), 50);
  })();

  return root;
}

/**
 * @param {{ search?: string }} [opts]
 */
export function renderSignup(opts) {
  const query = parseAuthSearch(opts && opts.search);
  if (query.oauth === 'workspace') return renderOAuthWorkspace();

  const err = errorBox('');
  const success = el('div', { class: 'auth-success', hidden: true });
  const email = el('input', {
    class: 'auth-fld', type: 'email', name: 'email',
    placeholder: 'you@company.com', required: true, autocomplete: 'email',
  });
  const org = el('input', {
    class: 'auth-fld', type: 'text', name: 'orgName',
    placeholder: 'Enter your company name', required: true,
  });
  const name = el('input', {
    class: 'auth-fld', type: 'text', name: 'name',
    placeholder: 'Your name', required: true, autocomplete: 'name',
  });
  const password = el('input', {
    class: 'auth-fld', type: 'password', name: 'password',
    placeholder: 'Minimum 6 characters', required: true, autocomplete: 'new-password',
  });
  const chatLanguage = languageSelect('signup-chat-language', 'chatLanguage');

  const form = el('form', {
    class: 'auth-form',
    onSubmit: async (event) => {
      event.preventDefault();
      const body = signupPayload({
        orgName: org.value,
        name: name.value,
        email: email.value,
        password: password.value,
        chatLanguage: chatLanguage.value,
      });
      const invalid = validateSignup(body);
      if (invalid) {
        setError(err, invalid);
        return;
      }
      setError(err, '');
      success.hidden = true;
      const data = await api('/auth/register', { method: 'POST', body });
      if (data && data.error) {
        setError(err, data.error);
        return;
      }
      if (data && data.accessToken) {
        await rememberSession(data);
        await goAfterSignup();
        return;
      }
      form.hidden = true;
      success.hidden = false;
      success.replaceChildren(
        el('h3', null, 'Workspace created'),
        el('p', null, (data && data.message) || 'Sign in to add your first product.'),
        el('button', {
          type: 'button',
          class: 'auth-go',
          onClick() { navigate('/app/login'); },
        }, 'Continue to sign in'),
      );
    },
  },
  el('label', { class: 'auth-field' },
    el('span', { class: 'lbl auth-field-lbl' }, 'Work email'), email),
  el('label', { class: 'auth-field' },
    el('span', { class: 'lbl auth-field-lbl' },
      'Company name ',
      el('span', { class: 'auth-field-hint' }, 'becomes your workspace')),
    org),
  el('label', { class: 'auth-field' },
    el('span', { class: 'lbl auth-field-lbl' }, 'Your name'), name),
  el('label', { class: 'auth-field' },
    el('span', { class: 'lbl auth-field-lbl' }, 'Password'), password),
  el('label', { class: 'auth-field' },
    el('span', { class: 'lbl auth-field-lbl' }, 'Preferred chat language'),
    chatLanguage),
  el('div', { class: 'auth-cta' },
    el('button', { type: 'submit', class: 'auth-go' }, 'Continue ', el('span', { class: 'auth-arw' }, '→')),
    el('span', { class: 'auth-alt' },
      'Already have an account? ',
      el('a', {
        href: '/app/login',
        class: 'auth-lnk',
        onClick(event) {
          event.preventDefault();
          navigate('/app/login');
        },
      }, 'Sign in'))));

  return authShell({
    kicker: '✦ Sign up',
    title: 'Create your ',
    titleEm: 'workspace.',
    lead: 'Continue with Google, Microsoft, or GitHub — then name your workspace. Or use email below.',
    error: err,
    children: [
      socialRow('signup', (provider) => {
        window.location.href = oauthStartUrl(provider, 'signup');
      }),
      el('div', { class: 'auth-or lbl' }, 'or use email'),
      form,
      success,
    ],
    foot: el('p', { class: 'auth-foot' },
      'Social login verifies your identity first; workspace name is asked right after. Connecting GitHub as a ',
      el('em', null, 'data source'),
      ' happens later, per product.'),
  });
}
